/**
 * MemorialBadges — Memorial Verification Badge chip strip.
 *
 * Displays the four-badge trust system on memorial detail + listing cards:
 *   ✅ Family Verified
 *   📜 Document Verified
 *   🏛 Historical Memorial
 *   🌳 Connected to Family Tree
 *
 * The backend (`memorials.py::_compute_verification_badges`) attaches the
 * `verification_badges` array to every memorial doc it returns, so this
 * component simply reads `memorial.verification_badges` — no fallback
 * derivation on the client.
 *
 * Two sizes:
 *   - `size="sm"` — compact icon-only chips for listing cards (tooltip on hover).
 *   - `size="md"` — labelled pill chips with icon + text for detail pages.
 */

export default function MemorialBadges({
  badges,
  size = "md",
  className = "",
  testidPrefix = "memorial-badge",
}) {
  const items = Array.isArray(badges) ? badges : [];
  if (!items.length) return null;

  const compact = size === "sm";
  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 ${className}`}
      data-testid={`${testidPrefix}-strip`}
    >
      {items.map((b) => (
        <span
          key={b.key}
          title={`${b.label} — ${b.description || ""}`}
          data-testid={`${testidPrefix}-${b.key}`}
          className={
            compact
              ? "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium leading-none whitespace-nowrap bg-white/90 backdrop-blur"
              : "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium leading-none bg-white shadow-sm"
          }
          style={{
            borderColor: b.color || "var(--wofy-border)",
            color: b.color || "var(--wofy-ink)",
          }}
        >
          <span aria-hidden="true" className={compact ? "text-[12px]" : "text-[14px]"}>
            {b.emoji}
          </span>
          <span className={compact ? "hidden md:inline" : ""}>{b.label}</span>
        </span>
      ))}
    </div>
  );
}
